import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import "./LandingPage.css";
import socialPawsLogo from "../../assets/socialPawsLogo.svg";

import step1Img from "../../assets/addToHomeSteps/step1.png";
import step2Img from "../../assets/addToHomeSteps/step2.png";
import step3Img from "../../assets/addToHomeSteps/step3.png";
import step4Img from "../../assets/addToHomeSteps/step4.png";
import step5Img from "../../assets/addToHomeSteps/step5.png";
import googleLoginIcon from "../../assets/googleLoginIcon.svg";
import appleLoginIcon from "../../assets/appleLoginIcon.svg";

import { useGlobalAlert } from "../../context/AlertContext";
import { auth, googleProvider, appleProvider } from "../../firebase-config";
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import apiClient from "../../api/apiClient";
import {
  setUserData,
  setUserId,
  fetchUserData,
  logout,
} from "../../redux/userSlice";
import { updateFcmToken } from "../../services/userService";

export const mapResponseToFormData = (responseJson) => {
  const data = responseJson?.data?.user
    ? responseJson.data
    : responseJson?.user
      ? responseJson
      : responseJson?.data || {};
  const user = data.user || {};
  const firstDog = data.dogs?.[0] || {};

  const formattedDogPhotos = {};
  if (firstDog.dog_images && firstDog.dog_images.length > 0) {
    formattedDogPhotos.main = firstDog.dog_images[0];
  }

  if (firstDog.dog_images && Array.isArray(firstDog.dog_images)) {
    const remainingImages = firstDog.dog_images.filter(
      (img) => img !== formattedDogPhotos.main,
    );
    if (remainingImages[0]) formattedDogPhotos.sub1 = remainingImages[0];
    if (remainingImages[1]) formattedDogPhotos.sub2 = remainingImages[1];
    if (remainingImages[2]) formattedDogPhotos.sub3 = remainingImages[2];
  }

  const formattedUserPhotos = {};
  if (user.user_profile_image && Array.isArray(user.user_profile_image)) {
    if (user.user_profile_image[0])
      formattedUserPhotos.main = user.user_profile_image[0];
    if (user.user_profile_image[1])
      formattedUserPhotos.sub1 = user.user_profile_image[1];
    if (user.user_profile_image[2])
      formattedUserPhotos.sub2 = user.user_profile_image[2];
    if (user.user_profile_image[3])
      formattedUserPhotos.sub3 = user.user_profile_image[3];
  }

  let whatsBringsYou = [];
  try {
    whatsBringsYou =
      typeof user.whats_brings_you === "string"
        ? JSON.parse(user.whats_brings_you)
        : user.whats_brings_you || firstDog.brings_you_here || [];
  } catch (e) {
    whatsBringsYou = firstDog.brings_you_here || [];
  }

  const rawGender = (firstDog.dog_gender || "").toLowerCase();
  const formattedGender =
    rawGender === "female" || rawGender === "girl"
      ? "girl"
      : rawGender === "male" || rawGender === "boy"
        ? "boy"
        : rawGender;

  const dogBreed = firstDog.dog_breed || "";
  const dogMixedBreed = Array.isArray(firstDog.dog_mixedBreed)
    ? firstDog.dog_mixedBreed
    : [];

  return {
    id: user.id || "",
    name: user.user_name || "",
    email: user.user_email || "",
    phone: user.user_phone || "",
    street: user.user_address || "",
    city: user.user_city || "",
    state: user.user_state || "",
    zipcode: user.user_zipcode || "",
    userBio: user.user_bio || "",
    team:
      user.user_dog_status === 1 || user.user_dog_status === true
        ? "paws"
        : "social",
    availability: data.availability || firstDog.availability || [],
    whatsBringsYou: whatsBringsYou,

    dogId: firstDog.id || "",
    dogName: firstDog.dog_name?.trim() || "",
    dogGender: formattedGender,
    dogBreed: dogBreed,
    selectedBreed1: dogMixedBreed[0] || "",
    selectedBreed2: dogMixedBreed[1] || "",
    selectedBreed3: dogMixedBreed[2] || "",
    dogMixedBreed: dogMixedBreed,
    dogActivity: firstDog.dog_activity?.toLowerCase() || "",
    dogAgeGroup: firstDog.age_group || "",
    dogBio: firstDog.bio || "",
    dogPersonality: firstDog.personality || [],
    dogTraits: firstDog.dog_traits || [],
    dogHobbies: firstDog.dog_hobbies || [],
    alongWithOtherDogs: firstDog.along_with_other_dogs || "",
    alongWithOtherHumans: firstDog.along_with_other_humans || "",
    pastExperienceWithDogs: firstDog.past_experience_with_dogs || "",
    pawsonalityCheck: firstDog.pawsonality_check || [],

    dogPhotos: formattedDogPhotos,
    photos: formattedUserPhotos,

    dogs: data.dogs || [],
    isSocialAuth: user.is_social_signup === 1 || user.is_social_signup === true,
    isNewUser: data.isNewUser || false,
    isOldUser: user.is_old_user === 1 || user.is_old_user === true,
    userDogStatus: user.user_dog_status,
    onboardingMode:
      user.is_old_user === 1 || user.is_old_user === true ? "update" : "create",
  };
};

const LandingPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { triggerAlert } = useGlobalAlert();

  const fcmToken = useSelector((state) => state.user.fcmToken);

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const [isMacSafari, setIsMacSafari] = useState(false);
  const [isMobileOS, setIsMobileOS] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);

  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);

  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);

        if (result) {
          const providerId = result.providerId;
          if (providerId === "apple.com") setIsAppleLoading(true);
          else setIsGoogleLoading(true);

          const firebaseUser = result.user;
          const providerName = providerId === "apple.com" ? "Apple" : "Google";

          let token;
          if (providerName === "Google") {
            const credential = GoogleAuthProvider.credentialFromResult(result);
            token = credential.idToken;
          } else {
            const credential = OAuthProvider.credentialFromResult(result);
            token = credential.idToken;
          }

          if (!token) {
            token = await firebaseUser.getIdToken();
          }

          const endpoint =
            providerName === "Apple"
              ? "/user/apple-signin"
              : "/user/google-signin";
          const payload =
            providerName === "Apple"
              ? { identityToken: token, ...(firebaseUser.displayName?.trim() ? { displayName: firebaseUser.displayName.trim() } : {}) }
              : { idToken: token };

          const response = await apiClient.post(endpoint, payload);
          const backendUser = response.data.data.user;
          const backendDogs = response.data.data.dogs;
          const backendToken = response.data.token || token;

          const userId = backendUser.id;

          const appleDisplayName = firebaseUser.displayName?.trim();
          if (providerName === "Apple" && appleDisplayName) {
            const updateForm = new FormData();
            updateForm.append("user_name", appleDisplayName);
            const updateResponse = await apiClient.put(
              `/user/update-user-byId/${userId}`,
              updateForm,
            );
            if (updateResponse.data?.status === false || updateResponse.data?.success === false) {
              throw new Error("Failed to update Apple profile name.");
            }
            backendUser.user_name = appleDisplayName;
          }

          sessionStorage.setItem("temp_old_user_token", backendToken);
          sessionStorage.setItem(
            "temp_old_user_data",
            JSON.stringify({
              ...backendUser,
              dogs: backendDogs || [],
            }),
          );

          if (
            backendUser.is_old_user === 1 ||
            backendUser.is_old_user === true
          ) {
            const mappedData = mapResponseToFormData(response.data);
            if (setFormData) {
              setFormData((prev) => ({ ...prev, ...mappedData }));
            }
          }

          if (fcmToken && userId) {
            try {
              await updateFcmToken(userId, fcmToken);
            } catch (fcmError) {
              console.error(
                "Failed to update FCM token during sign-in:",
                fcmError,
              );
            }
          }

          if (providerName !== "Apple" && (!backendUser.user_name || backendUser.user_name.trim() === "")) {
            const updateForm = new FormData();
            if (firebaseUser.displayName)
              updateForm.append("user_name", firebaseUser.displayName);
            if (firebaseUser.photoURL) {
              updateForm.append("images", firebaseUser.photoURL);
              updateForm.append("image_index", 0);
            }
            await apiClient.put(`/user/update-user-byId/${userId}`, updateForm);
          }

          dispatch(setUserData({ token: backendToken, data: backendUser }));
          dispatch(setUserId(userId));
          await dispatch(fetchUserData(userId)).unwrap();

          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const pos = {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                };
                localStorage.setItem(
                  "user_location_coords",
                  JSON.stringify(pos),
                );
              },
              (error) => console.warn("Location denied.", error),
              { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
            );
          }

          const firstDog = backendDogs?.[0];
          const isDogProfileComplete =
            firstDog?.isDogProfileComplete === 1 ||
            firstDog?.isDogProfileComplete === true;
          const isDogProfileHalfComplete =
            firstDog?.isDogProfileHalfComplete === 1 ||
            firstDog?.isDogProfileHalfComplete === true;

          if (
            backendUser.is_old_user === 1 ||
            backendUser.is_old_user === true
          ) {
            if (isDogProfileComplete) {
              navigate("/dashboard-switcher");
              triggerAlert(
                `Successfully authenticated with ${providerName}!`,
                "success",
              );
            } else {
              if (
                backendUser.user_dog_status === 0 ||
                backendUser.user_dog_status === false
              ) {
                if (window.innerWidth >= 1024) {
                  navigate("/team-social-bio");
                } else {
                  navigate("/dog-social-personality", { state: { step: 1 } });
                }
              } else {
                navigate("/dog-details");
              }
              triggerAlert("Please continue your onboarding flow", "success");
            }
          } else {
            if (isDogProfileComplete || isDogProfileHalfComplete) {
              navigate("/dashboard-switcher");
            } else {
              navigate("/onboarding");
            }
            triggerAlert(
              `Successfully authenticated with ${providerName}!`,
              "success",
            );
          }
        }
      } catch (error) {
        console.error("Redirect login error:", error);
        triggerAlert("Failed to complete login after redirect.", "error");
      } finally {
        setIsGoogleLoading(false);
        setIsAppleLoading(false);
      }
    };

    handleRedirectResult();
  }, [dispatch, fcmToken, navigate, setFormData, triggerAlert]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      console.log("User accepted the install prompt");
    }
    setDeferredPrompt(null);
    setShowPromptModal(false);
  };

  const handleSocialAuth = async (providerName) => {
    if (
      (providerName === "Google" && isGoogleLoading) ||
      (providerName === "Apple" && isAppleLoading)
    ) {
      return;
    }

    providerName === "Google"
      ? setIsGoogleLoading(true)
      : setIsAppleLoading(true);

    try {
      const provider =
        providerName === "Apple" ? appleProvider : googleProvider;

      const result = await signInWithPopup(auth, provider);
      const firebaseUser = result.user;

      let token;
      if (providerName === "Google") {
        const credential = GoogleAuthProvider.credentialFromResult(result);
        token = credential.idToken;
      } else if (providerName === "Apple") {
        const credential = OAuthProvider.credentialFromResult(result);
        token = credential.idToken;
      }

      if (!token) token = await firebaseUser.getIdToken();

      const endpoint =
        providerName === "Apple" ? "/user/apple-signin" : "/user/google-signin";
      const payload =
        providerName === "Apple"
          ? { identityToken: token, ...(firebaseUser.displayName?.trim() ? { displayName: firebaseUser.displayName.trim() } : {}) }
          : { idToken: token };

      const response = await apiClient.post(endpoint, payload);
      const backendUser = response.data.data.user;
      const backendDogs = response.data.data.dogs;
      const backendToken = response.data.token || token;

      const userId = backendUser.id;

      const appleDisplayName = firebaseUser.displayName?.trim();
      if (providerName === "Apple" && appleDisplayName) {
        const updateForm = new FormData();
        updateForm.append("user_name", appleDisplayName);
        const updateResponse = await apiClient.put(
          `/user/update-user-byId/${userId}`,
          updateForm,
        );
        if (updateResponse.data?.status === false || updateResponse.data?.success === false) {
          throw new Error("Failed to update Apple profile name.");
        }
        backendUser.user_name = appleDisplayName;
      }

      sessionStorage.setItem("temp_old_user_token", backendToken);
      sessionStorage.setItem(
        "temp_old_user_data",
        JSON.stringify({
          ...backendUser,
          dogs: backendDogs || [],
        }),
      );

      // Only set formData if the user is an old user (1 or true)
      if (backendUser.is_old_user === 1 || backendUser.is_old_user === true) {
        const mappedData = mapResponseToFormData(response.data);
        if (setFormData) {
          setFormData((prev) => ({ ...prev, ...mappedData }));
        }
      }

      if (fcmToken && userId) {
        try {
          await updateFcmToken(userId, fcmToken);
        } catch (fcmError) {
          console.error("Failed to update FCM token:", fcmError);
        }
      }

      if (providerName !== "Apple" && (!backendUser.user_name || backendUser.user_name.trim() === "")) {
        const updateForm = new FormData();
        if (firebaseUser.displayName)
          updateForm.append("user_name", firebaseUser.displayName);
        if (firebaseUser.photoURL) {
          updateForm.append("images", firebaseUser.photoURL);
          updateForm.append("image_index", 0);
        }
        await apiClient.put(`/user/update-user-byId/${userId}`, updateForm);
      }

      dispatch(setUserData({ token: backendToken, data: backendUser }));
      dispatch(setUserId(userId));
      await dispatch(fetchUserData(userId)).unwrap();

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const pos = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            localStorage.setItem("user_location_coords", JSON.stringify(pos));
          },
          (error) => console.warn("Location denied.", error),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
        );
      }

      const firstDog = backendDogs?.[0];
      const isDogProfileComplete =
        firstDog?.isDogProfileComplete === 1 ||
        firstDog?.isDogProfileComplete === true;
      const isDogProfileHalfComplete =
        firstDog?.isDogProfileHalfComplete === 1 ||
        firstDog?.isDogProfileHalfComplete === true;

      if (backendUser.is_old_user === 1 || backendUser.is_old_user === true) {
        if (isDogProfileComplete) {
          navigate("/dashboard-switcher");
          triggerAlert(
            `Successfully authenticated with ${providerName}!`,
            "success",
          );
        } else {
          if (
            backendUser.user_dog_status === 0 ||
            backendUser.user_dog_status === false
          ) {
            if (window.innerWidth >= 1024) {
              navigate("/team-social-bio");
            } else {
              navigate("/dog-social-personality", { state: { step: 1 } });
            }
          } else {
            navigate("/dog-details");
          }
          triggerAlert("Please continue your onboarding flow", "success");
        }
      } else {
        if (isDogProfileComplete || isDogProfileHalfComplete) {
          navigate("/dashboard-switcher");
        } else {
          navigate("/onboarding");
        }
        triggerAlert(
          `Successfully authenticated with ${providerName}!`,
          "success",
        );
      }
    } catch (error) {
      console.error(`${providerName} login error:`, error);
      if (error.code === "auth/popup-closed-by-user") {
        triggerAlert("Login popup was closed before finishing.", "error");
      } else if (error.code === "auth/popup-blocked") {
        triggerAlert("Popup blocked by browser. Please allow popups.", "error");
      } else if (error.code !== "auth/cancelled-popup-request") {
        triggerAlert(`Failed to authenticate with ${providerName}.`, "error");
      }
    } finally {
      if (providerName === "Google") setIsGoogleLoading(false);
      else setIsAppleLoading(false);
    }
  };

  const handleLoginPage = () => navigate("/login");

  return (
    <div className="sp-container">
      <div className="sp-hero-section">
        <div className="sp-hero-bg"></div>
        <div className="sp-overlay"></div>

        <div className="sp-logo-area">
          <div className="sp-logo-icon">
            <img
              src={socialPawsLogo}
              alt="Social Paws Logo"
              className="sp-logo-img"
            />
          </div>
          <span className="sp-logo-text">Social Paws</span>
        </div>

        <div className="sp-hero-content">
          <h1 className="sp-hero-title">
            THE FUTURE <br /> IS DOG-SHARING
          </h1>
          <p className="sp-hero-subtitle">WHERE EVERYONE GETS TO LOVE A DOG</p>
        </div>
      </div>

      <div className="sp-action-section">
        <nav className="sp-nav">
          <div className="sp-desktop-nav">
            <a
              href="https://www.socialpawsapp.com/team"
              target="_blank"
              rel="noopener noreferrer"
              className="sp-nav-link"
            >
              About
            </a>
            <a
              href="https://www.socialpawsapp.com/events"
              target="_blank"
              rel="noopener noreferrer"
              className="sp-nav-link"
            >
              Events
            </a>
          </div>
        </nav>

        <div className="sp-content-wrapper">
          <div className="sp-main-content">
            <div className="sp-btn-group">
              <button
                className="sp-btn-social"
                onClick={() => handleSocialAuth("Google")}
                disabled={isGoogleLoading || isAppleLoading}
                style={{
                  opacity: isGoogleLoading ? 0.6 : 1,
                  cursor: isGoogleLoading ? "not-allowed" : "pointer",
                }}
              >
                <img
                  src={googleLoginIcon}
                  alt="Google"
                  style={{ width: "22px" }}
                />
                {isGoogleLoading ? "Loading..." : "Continue with Google"}
              </button>

              <button
                className="sp-btn-social"
                onClick={() => handleSocialAuth("Apple")}
                disabled={isGoogleLoading || isAppleLoading}
                style={{
                  opacity: isAppleLoading ? 0.6 : 1,
                  cursor: isAppleLoading ? "not-allowed" : "pointer",
                }}
              >
                <img src={appleLoginIcon} alt="Apple" />
                {isAppleLoading ? "Loading..." : "Continue with Apple"}
              </button>
            </div>

            <h2 className="sp-main-headline">
              New here? We'll set up your profile.
              <br />
              Already a member? You'll be logged in.
            </h2>
          </div>

          {/* <button
            onClick={handleLoginPage}
            style={{
              background: "none",
              border: "none",
              color: "rgba(255, 255, 255, 0.9)",
              fontSize: "1.1rem",
              marginTop: "1.5rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              fontWeight: "500",
              textDecoration: "underline",
              textUnderlineOffset: "4px",
              marginBottom: "2rem",
            }}
          >
            Use different login method
          </button> */}

          <nav className="sp-mobile-nav">
            <a
              href="https://www.socialpawsapp.com/how_its_work"
              target="_blank"
              rel="noopener noreferrer"
              className="sp-nav-link"
            >
              About
            </a>
            <a
              href="https://www.socialpawsapp.com/events"
              target="_blank"
              rel="noopener noreferrer"
              className="sp-nav-link"
            >
              Events
            </a>
          </nav>

          <div className="sp-footer">
            <p className="sp-footer-text">
              By signing up, you agree to our{" "}
              <a
                href="https://www.socialpawsapp.com/cms/TC-PAWS"
                target="_blank"
                rel="noopener noreferrer"
                className="sp-footer-link"
              >
                Terms and Conditions
              </a>
              <br />
              <a
                href="https://www.socialpawsapp.com/cms/PAWS"
                target="_blank"
                rel="noopener noreferrer"
                className="sp-footer-link"
              >
                Privacy Pawlicy
              </a>
              , and{" "}
              <a
                href="https://www.socialpawsapp.com/cms/community-guideline"
                target="_blank"
                rel="noopener noreferrer"
                className="sp-footer-link"
              >
                Community Guidelines
              </a>
            </p>
            <p className="sp-footer-text" style={{ marginTop: "0.5rem" }}>
              Feedback?{" "}
              <a
                href="https://forms.gle/Tc6S4mkY3UCWrFLk7"
                target="_blank"
                rel="noopener noreferrer"
                className="sp-footer-link"
                style={{ fontWeight: "bold" }}
              >
                Submit here
              </a>
            </p>
          </div>
        </div>
      </div>

      {showPromptModal && (
        <div
          className="sp-install-overlay"
          onClick={() => setShowPromptModal(false)}
        >
          <div
            className="sp-install-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="sp-install-close"
              onClick={() => setShowPromptModal(false)}
            >
              ✕
            </button>
            <div className="sp-install-header">
              <div className="sp-install-icon-wrapper">
                <img src={socialPawsLogo} alt="App Icon" />
              </div>
              <h3 className="sp-install-title">Install Social Paws</h3>
            </div>
            <p className="sp-install-desc">
              Install the app on your device to easily access it anytime.
            </p>
            {isIOSDevice ? (
              <div className="sp-ios-instructions">
                <p>
                  1. Tap on the share icon{" "}
                  <svg
                    className="sp-ios-share-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                    <polyline points="16 6 12 2 8 6"></polyline>
                    <line x1="12" y1="2" x2="12" y2="15"></line>
                  </svg>
                </p>
                <p>
                  2. Select&nbsp;<strong>Add to Home Screen</strong>
                </p>
              </div>
            ) : isMacSafari ? (
              <div className="sp-ios-instructions">
                <p>
                  1. Click the share icon{" "}
                  <svg
                    className="sp-ios-share-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                    <polyline points="16 6 12 2 8 6"></polyline>
                    <line x1="12" y1="2" x2="12" y2="15"></line>
                  </svg>
                  &nbsp; at the top of Safari.
                </p>
                <p>
                  2. Select&nbsp;<strong>Add to Dock</strong>
                </p>
              </div>
            ) : (
              <button
                className="sp-install-action-btn"
                onClick={handleInstallClick}
              >
                Install
              </button>
            )}
          </div>
        </div>
      )}

      {showInstallModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "1rem",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "450px",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              boxShadow: "0px 10px 30px rgba(0,0,0,0.5)",
              maxHeight: "90vh",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0, fontWeight: "700" }}>
                Install Social Paws
              </h3>
              <button
                onClick={() => setShowInstallModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <X size={24} color="rgba(166, 28, 31, 1)" />
              </button>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1.5rem",
                overflowY: "auto",
                paddingRight: "0.5rem",
              }}
            >
              <div>
                <p style={{ margin: "0 0 0.5rem 0", fontWeight: "600" }}>
                  Step 1: Open the browser's share menu.
                </p>
                <img
                  src={step1Img}
                  alt="Step 1"
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
              <div>
                <p style={{ margin: "0 0 0.5rem 0", fontWeight: "600" }}>
                  Step 2: Select "More" in the bottom row.
                </p>
                <img
                  src={step2Img}
                  alt="Step 2"
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
              <div>
                <p style={{ margin: "0 0 0.5rem 0", fontWeight: "600" }}>
                  Step 3: Look for "Share" in the list.
                </p>
                <img
                  src={step3Img}
                  alt="Step 3"
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
              <div>
                <p style={{ margin: "0 0 0.5rem 0", fontWeight: "600" }}>
                  Step 4: Select "Add to Home Screen".
                </p>
                <img
                  src={step4Img}
                  alt="Step 4"
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
              <div>
                <p style={{ margin: "0 0 0.5rem 0", fontWeight: "600" }}>
                  Step 5: Tap "Add" to finish installation.
                </p>
                <img
                  src={step5Img}
                  alt="Step 5"
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
